import React, { useEffect, useRef } from 'react';
import OktaSignIn from '@okta/okta-signin-widget';
import { Tokens } from '@okta/okta-auth-js';
import '@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
import { getOktaAuth } from '../../providers/AuthProvider';
import { useSearchParams } from 'react-router-dom';
import { useAnalytics } from '../../hooks/analytics';

const OktaSignInWidget = (props: any) => {
  const { onSuccess, onError } = props;
  const widgetRef = useRef(null);

  const [searchParams] = useSearchParams();
  const analytics = useAnalytics();

  const activationToken = searchParams.get('activationToken');
  const resetToken = searchParams.get('resetToken');

  const removeRegisterHelpLink = () => {
    // v1 workaround: remove the register help link rendered by the Okta widget.
    const registerAnchors = document.querySelectorAll<HTMLAnchorElement>(
      'a[href="register"], a[href="/register"]',
    );

    registerAnchors.forEach((anchor) => {
      const container = anchor.closest('li') ?? anchor.parentElement;
      container?.remove();
    });
  };

  const promoteResetPasswordLink = () => {
    const widgetRoot = widgetRef.current as HTMLElement | null;
    if (!widgetRoot) {
      return;
    }

    const footer = widgetRoot.querySelector<HTMLElement>('.auth-footer');
    const forgotPasswordLink = widgetRoot.querySelector<HTMLAnchorElement>(
      'a[data-se="forgot-password"]',
    );

    if (!footer || !forgotPasswordLink) {
      return;
    }

    const existingPromotedLink = widgetRoot.querySelector<HTMLAnchorElement>(
      'a[data-caps-promoted-reset="true"]',
    );

    if (!existingPromotedLink) {
      const promotedLink = forgotPasswordLink.cloneNode(true) as HTMLAnchorElement;
      promotedLink.setAttribute('data-caps-promoted-reset', 'true');

      promotedLink.addEventListener('click', (event) => {
        event.preventDefault();

        const activeForgotPasswordLink = widgetRoot.querySelector<HTMLAnchorElement>(
          'a[data-se="forgot-password"]',
        );
        activeForgotPasswordLink?.click();
      });

      footer.appendChild(promotedLink);
    }

    const helpToggleLink = widgetRoot.querySelector<HTMLElement>('a[data-se="needhelp"]');
    const helpLinksContainer = widgetRoot.querySelector<HTMLElement>('#help-links-container');

    helpToggleLink?.style.setProperty('display', 'none');
    helpLinksContainer?.style.setProperty('display', 'none');
  };

  const addTermsOfUseAcknowledgment = () => {
    const widgetRoot = widgetRef.current as HTMLElement | null;
    if (!widgetRoot) {
      return;
    }

    const footer = widgetRoot.querySelector<HTMLElement>('.auth-footer');
    if (!footer) {
      return;
    }

    const existingTermsOfUseAcknowledgment = footer.querySelector<HTMLElement>(
      '[data-caps-tou-link="true"]',
    );

    if (existingTermsOfUseAcknowledgment) {
      return;
    }

    const termsOfUseAcknowledgment = document.createElement('div');
    termsOfUseAcknowledgment.setAttribute('data-caps-tou-link', 'true');
    termsOfUseAcknowledgment.style.marginTop = '8px';
    termsOfUseAcknowledgment.style.textAlign = 'center';
    termsOfUseAcknowledgment.style.fontSize = '12px';
    termsOfUseAcknowledgment.style.color = '#6f6f6f';

    const prefix = document.createTextNode('By signing in, you agree to our ');
    const termsOfUseLink = document.createElement('a');
    termsOfUseLink.href = 'https://forgeglobal.com/terms-of-use/';
    termsOfUseLink.target = '_blank';
    termsOfUseLink.rel = 'noopener noreferrer';
    termsOfUseLink.textContent = 'Terms of Use';

    termsOfUseAcknowledgment.appendChild(prefix);
    termsOfUseAcknowledgment.appendChild(termsOfUseLink);

    footer.appendChild(termsOfUseAcknowledgment);
  };

  const applyHelpLinkWorkarounds = () => {
    removeRegisterHelpLink();
    promoteResetPasswordLink();
    addTermsOfUseAcknowledgment();
  };

  useEffect(() => {
    const config = getOktaAuth();
    const extraConfig = {
      //logo: "https://forgeglobal.com/site/images/forge_logo.svg",
      helpLinks: {
        help: 'register',
        forgotPassword: 'resetpassword',
      },
      language: 'en',
      i18n: {
        en: {
          needhelp: 'Need help? Click for more options...',
          help: "Don't have an account? Sign up",
          forgotpassword: 'Reset Password',
          // Labels
          'primaryauth.title': 'Sign In (Document Portal)',
          'primaryauth.username.tooltip': 'Email',
          'primaryauth.password.tooltip': 'Password ',
          'password.reset.title.generic': 'Reset Password',
          'enroll.choices.description.generic':
            'Forge requires multifactor authentication to add an additional layer of security when signing in to your account',
          // Errors
          'error.username.required': 'Please enter your email',
          'error.password.required': 'Please enter your password',
          'errors.E0000004': 'Sign in failed!',
        },
      },
    };

    const recoveryToken = activationToken ?? resetToken;
    if (recoveryToken) {
      config.options.recoveryToken = recoveryToken;
    }

    const widget = new OktaSignIn({ ...extraConfig, ...config.options });
    widget.on('ready', applyHelpLinkWorkarounds);
    widget.on('afterRender', applyHelpLinkWorkarounds);

    widget
      .showSignInToGetTokens({
        el: widgetRef.current as any,
      })
      .then((tokens: Tokens) => {
        const user = tokens.accessToken?.claims.sub;
        if (user) {
          analytics.identifyUser(user, {});
        }
        onSuccess(tokens);
      })
      .catch(onError);

    return () => widget.remove();
  }, [onSuccess, onError]);


  return <div ref={widgetRef} />;
};

export default OktaSignInWidget;
